import React, { useState, useEffect, useRef } from 'react';
import * as Styled from './MParagraph.styles';
import clsx from 'clsx';

import { MParagraphProps, MParagraphAlignment } from './MParagraph.types';

import { ABody, ABodySize, ABodyColor } from '../../atoms/ABody';
import { ALinkStandalone, ALinkStandaloneSize } from '../../atoms/ALinkStandalone';

const COMPONENT_NAME = 'm-web-ui-kit-MParagraph';
const TEST_ID = 'web-ui-kit-MParagraph';
// TODO: Get I18n keys for these hardcoded tokens
const TEXT_I18N_SEE_MORE = 'See More';
const TEXT_I18N_SEE_LESS = 'See Less';

export const MParagraph: React.FC<MParagraphProps> = ({
  alignment = MParagraphAlignment.Left,
  truncated = false,
  truncateLines = 3,
  expanded = false,
  bodySize = ABodySize.S,
  bodyColor = ABodyColor.Default,
  truncationLinkSize = ALinkStandaloneSize.Small,
  children,
  className,
}) => {
  const [isExpanded, setExpanded] = useState(truncated ? expanded : true);
  const [showTruncationControl, setShowTruncationControl] = useState(truncated);
  const fullText = children;
  const truncateClass = `m-ellipsis-${truncateLines > 1 ? Math.min(truncateLines, 7) + 'lines' : '1line'}`;
  const paragraphRef = useRef<HTMLDivElement>(null);

  function manageTruncationControlVisibility(isTruncated: boolean) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const { offsetHeight, scrollHeight } = paragraphRef.current ?? {};
        // we're not truncating, so hide the control
        if (!isTruncated) {
          setShowTruncationControl(false);
          return;
        }
        // do nothing when we cannot measure overflow
        if (!offsetHeight || !scrollHeight) {
          return;
        }

        const isOverflowing = offsetHeight < scrollHeight;
        setShowTruncationControl(!isExpanded && isOverflowing);
      });
    });
  }

  function handleResize() {
    manageTruncationControlVisibility(truncated);
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    setExpanded(!truncated);
    manageTruncationControlVisibility(truncated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truncated]);

  useEffect(() => {
    setExpanded(truncated ? expanded : true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    setExpanded(!isExpanded);
  }

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      alignment={alignment}
      className={className}
    >
      <Styled.ParagraphContent ref={paragraphRef} className={clsx(!isExpanded && truncateClass)}>
        <ABody bodyText={fullText || ''} size={bodySize} color={bodyColor} />
      </Styled.ParagraphContent>
      {showTruncationControl && (
        <Styled.TruncationControl>
          <ALinkStandalone onClick={handleClick} href="#" size={truncationLinkSize}>
            {isExpanded ? TEXT_I18N_SEE_LESS : TEXT_I18N_SEE_MORE}
          </ALinkStandalone>
        </Styled.TruncationControl>
      )}
    </Styled.Wrapper>
  );
};

export { MParagraphAlignment, TEST_ID as M_PARAGRAPH_TEST_ID };
